import { useEffect, useState } from 'react'
import { Building2, Plus, Pencil, Trash2, Save, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuth } from '../hooks/useAuth.jsx'
import empresasService from '../services/empresasService'
import FieldHelp from '../components/FieldHelp'

const VACIO = {
  codigo: '',
  nombre: '',
  token: '',
  instance_id: '',
  base_url: 'https://crmwp.tecnytte.com/api/v1/send-text',
  activo: 1
}

function GestionEmpresas() {
  const { hasPermission } = useAuth()
  const [empresas, setEmpresas] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editando, setEditando] = useState(null)
  const [form, setForm] = useState(VACIO)

  const cargar = async () => {
    try {
      setLoading(true)
      const response = await empresasService.getEmpresas()
      setEmpresas(response.data || [])
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al cargar empresas')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    cargar()
  }, [])

  const abrirNuevo = () => {
    setForm(VACIO)
    setEditando('nuevo')
  }

  const abrirEditar = (empresa) => {
    setForm({
      codigo: empresa.codigo || '',
      nombre: empresa.nombre || '',
      token: empresa.token || '',
      instance_id: empresa.instance_id || '',
      base_url: empresa.base_url || VACIO.base_url,
      activo: empresa.activo === 0 || empresa.activo === '0' ? 0 : 1
    })
    setEditando(empresa.id)
  }

  const cancelar = () => {
    setEditando(null)
    setForm(VACIO)
  }

  const guardar = async (e) => {
    e.preventDefault()
    try {
      setSaving(true)
      if (editando === 'nuevo') {
        await empresasService.createEmpresa(form)
        toast.success('Empresa creada')
      } else {
        await empresasService.updateEmpresa(editando, form)
        toast.success('Empresa actualizada')
      }
      cancelar()
      cargar()
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar la empresa')
    } finally {
      setSaving(false)
    }
  }

  const eliminar = async (empresa) => {
    if (!confirm(`¿Eliminar la empresa ${empresa.codigo}? Solo se puede si no hay clientes usándola.`)) {
      return
    }
    try {
      await empresasService.deleteEmpresa(empresa.id)
      toast.success('Empresa eliminada')
      cargar()
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al eliminar la empresa')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            Gestión de Empresas
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Cada empresa es un bot de WhatsApp (token + instance id). El cliente elige el código y el servicio automático envía por esa instancia.
          </p>
        </div>
        {hasPermission('AGREGAR_EMPRESA') && (
          <button className="btn btn-success" onClick={abrirNuevo}>
            <Plus className="w-4 h-4 mr-2" />
            Nueva empresa
          </button>
        )}
      </div>

      {editando && (
        <div className="card mb-6">
          <div className="card-body">
            <form onSubmit={guardar} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Código *</label>
                  <input
                    className="form-input"
                    value={form.codigo}
                    onChange={(e) => setForm({ ...form, codigo: e.target.value.trim().toUpperCase().replace(/\s+/g, '') })}
                    placeholder="TECNYTTEGPS"
                    required
                  />
                  <FieldHelp>Clave exacta que se guarda en el cliente. Sin espacios. Ejemplo: TECNYTTEGPS</FieldHelp>
                </div>
                <div>
                  <label className="form-label">Nombre visible *</label>
                  <input
                    className="form-input"
                    value={form.nombre}
                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                    placeholder="Tecnytte GPS"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Token WhatsApp *</label>
                  <textarea
                    className="form-input"
                    rows="2"
                    value={form.token}
                    onChange={(e) => setForm({ ...form, token: e.target.value })}
                    required
                  />
                  <FieldHelp>Token de la instancia en crmwp.tecnytte.com</FieldHelp>
                </div>
                <div>
                  <label className="form-label">Instance ID *</label>
                  <textarea
                    className="form-input"
                    rows="2"
                    value={form.instance_id}
                    onChange={(e) => setForm({ ...form, instance_id: e.target.value })}
                    required
                  />
                  <FieldHelp>ID de la instancia WhatsApp asociada a este bot</FieldHelp>
                </div>
                <div>
                  <label className="form-label">URL API</label>
                  <input
                    className="form-input"
                    value={form.base_url}
                    onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                  />
                </div>
                <div>
                  <label className="form-label">Estado</label>
                  <select
                    className="form-select"
                    value={form.activo}
                    onChange={(e) => setForm({ ...form, activo: Number(e.target.value) })}
                  >
                    <option value={1}>Activa (se puede usar para enviar)</option>
                    <option value={0}>Inactiva</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" className="btn btn-secondary" onClick={cancelar}>
                  <X className="w-4 h-4 mr-1" /> Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  <Save className="w-4 h-4 mr-1" /> {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body p-0 overflow-x-auto">
          <table className="table min-w-full text-sm">
            <thead>
              <tr>
                <th className="px-3 py-2">Código</th>
                <th className="px-3 py-2">Nombre</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {empresas.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-3 py-6 text-center text-gray-500">
                    No hay empresas. Cree las 3 actuales (TECNYTTEGPS, TECNYTTESOLUCIONES, TECNYTTESIMULACIONES) o ejecute el SQL de semilla.
                  </td>
                </tr>
              ) : empresas.map((empresa) => (
                <tr key={empresa.id}>
                  <td className="px-3 py-2 font-mono">{empresa.codigo}</td>
                  <td className="px-3 py-2">{empresa.nombre}</td>
                  <td className="px-3 py-2">
                    <span className={`badge ${String(empresa.activo) !== '0' ? 'badge-success' : 'badge-secondary'}`}>
                      {String(empresa.activo) !== '0' ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center gap-1">
                      {hasPermission('EDITAR_EMPRESA') && (
                        <button className="btn btn-warning btn-sm p-1" title="Editar" onClick={() => abrirEditar(empresa)}>
                          <Pencil className="w-3 h-3" />
                        </button>
                      )}
                      {hasPermission('ELIMINAR_EMPRESA') && (
                        <button className="btn btn-danger btn-sm p-1" title="Eliminar" onClick={() => eliminar(empresa)}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default GestionEmpresas
